import ContextualSaveBar from "@/components/bars/ContextualSaveBar";
import { Card, CardContent, CardHeader } from "@/components/layouts/Card";
import PageLoader from "@/components/loader/PageLoader";
import OptionListSelectionModal from "@/components/modals/OptionListSelectionModal";
import ReorderableList from "@/components/modals/ReorderableList";
import CategoryOperatingHoursInput from "@/components/selectors/CategoryOperatingHoursInput";
import { Input } from "@/components/ui/input";
import { useUser } from "@/contexts/UserContext";
import { Category, OperatingHour, OptionList } from "@/utils/types";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

const EditCategory = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { id: categoryId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [axiosInstance, loading, isFetching] = useAxiosInstance();
  const [category, setCategory] = useState<Category>({
    id: 0,
    name: "",
    description: "",
    status: "",
    parent_category: null,
    category_option_lists: [],
    category_hours: [],
  });
  const [operatingHours, setOperatingHours] = useState<OperatingHour[]>([]);
  const [isOptionListModalOpen, setIsOptionListModalOpen] = useState(false);
  const [originalCategory, setOriginalCategory] = useState<Category | null>(
    null,
  );
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchCategory = async () => {
      if (loading || !axiosInstance) return;
      try {
        const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}${
          import.meta.env.VITE_APP_REDBIRDPOSBE_CATEGORY_INFORMATION
        }`;

        const response = await axiosInstance.get(`${apiUrl}${categoryId}/`);
        setCategory({
          ...response.data,
          category_option_lists: response.data.category_option_lists || [],
        });
        setOriginalCategory(response.data);
        setOperatingHours(response.data.category_hours || []);
      } catch (error) {
        console.error("Error fetching category:", error);
        toast.error("Failed to fetch category information");
      }
    };

    if (categoryId) {
      fetchCategory();
    }
  }, [axiosInstance, loading, categoryId, user?.selectedBranch]);

  useEffect(() => {
    if (originalCategory) {
      const isChanged =
        JSON.stringify({ ...category, category_hours: operatingHours }) !==
        JSON.stringify({
          ...originalCategory,
          category_hours: originalCategory.category_hours,
        });
      setIsDirty(isChanged);
    }
  }, [category, originalCategory, operatingHours]);

  const handleSubmit = async () => {
    if (loading || !axiosInstance || !user?.selectedBranch) return;
    setIsSaving(true);
    try {
      const apiUrl =
        import.meta.env.VITE_APP_REDBIRDPOSBE_CATEGORY_INFORMATION ?? "";

      const transformedOptionLists = category.category_option_lists.map(
        (optionList) => ({
          id: optionList.id,
          name: optionList.name,
          branch: optionList.branch,
          options: optionList.options.map((option) => option.id),
          required: optionList.required,
          option_type: optionList.option_type,
          max_selections: optionList.max_selections,
        }),
      );

      const updatedCategory = {
        name: category.name,
        description: category.description,
        status: category.status,
        branch: user.selectedBranch.id,
        parent_category: category.parent_category || null,
        category_option_lists: transformedOptionLists,
        ranking: category.ranking,
        category_hours: operatingHours,
      };

      await axiosInstance.put(`${apiUrl}${categoryId}/`, updatedCategory);

      toast.success(t("back.management.menu.category.successupdate"));
      setIsDirty(false);
      navigate("/menu/categories");
    } catch (error) {
      console.error("Error updating category:", error);
      toast.error(t("back.management.menu.category.updatefail"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleOptionListSave = (selectedOptionLists: OptionList[]) => {
    const updatedOptionLists = selectedOptionLists.map((optionList) => {
      const existingOptionList = category.category_option_lists.find(
        (existingList) => existingList.id === optionList.id,
      );

      return existingOptionList
        ? { ...existingOptionList, ...optionList }
        : optionList;
    });

    setCategory((prevCategory) => ({
      ...prevCategory,
      category_option_lists: updatedOptionLists,
    }));

    setIsOptionListModalOpen(false);
  };

  const handleRemoveOptionList = (optionList: OptionList) => {
    setCategory((prevCategory) => ({
      ...prevCategory,
      category_option_lists: prevCategory.category_option_lists.filter(
        (list) => list.id !== optionList.id,
      ),
    }));
  };

  const handleDiscard = () => {
    if (originalCategory) {
      setCategory(originalCategory);
      setOperatingHours(originalCategory.category_hours || []);
    }
  };

  return (
    <PageLoader isFetching={isFetching}>
      <div className="container mx-auto px-4 py-2">
        <h1 className="text-xl font-bold">
          {t("back.management.menu.category.editcategory")}
        </h1>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className="my-4 rounded-lg bg-white p-4 shadow">
            <h2 className="text-lg font-semibold">
              {t("back.management.menu.category.generalinfo")}
            </h2>
            <div className="-mx-2 flex flex-wrap">
              <div className="mb-4 w-full px-2 md:mb-0 md:w-full">
                <label
                  className="mb-2 block text-sm font-bold text-gray-700"
                  htmlFor="categoryName"
                >
                  {t("back.management.menu.category.categoryname")}
                </label>
                <input
                  type="text"
                  id="name"
                  placeholder="Enter category name"
                  value={category.name}
                  onChange={(e) =>
                    setCategory({ ...category, name: e.target.value })
                  }
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                />
              </div>
            </div>
            <div className="mt-4">
              <label
                className="mb-2 block text-sm font-bold text-gray-700"
                htmlFor="description"
              >
                {t("back.management.menu.category.description")}
              </label>
              <textarea
                id="description"
                placeholder="Enter category description"
                value={category.description}
                onChange={(e) =>
                  setCategory({ ...category, description: e.target.value })
                }
                className="block h-48 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
              />
            </div>
            <div className="mt-4 flex items-center space-x-4">
              <label
                className="mb-2 block text-sm font-bold text-gray-700"
                htmlFor="status"
              >
                {t("back.management.menu.category.status")}
              </label>
              <Input
                className="h-6 w-6 text-center"
                type="checkbox"
                id="status"
                checked={category.status === "Active"}
                onChange={(e) =>
                  setCategory({
                    ...category,
                    status: e.target.checked ? "Active" : "Inactive",
                  })
                }
              />
              <span>
                {category.status === "Active" ? "Active" : "Inactive"}
              </span>
            </div>
          </div>

          <Card className="mb-4">
            <CardHeader className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {t("Linked OptionLists")}
              </h2>
              <button
                type="button"
                onClick={() => setIsOptionListModalOpen(true)}
                className="flex items-center rounded-md bg-gray-500 px-3 py-1 text-white"
              >
                {t("Edit")}
              </button>
            </CardHeader>
            <CardContent>
              <ReorderableList
                items={category.category_option_lists}
                onReorder={(newOptionList) =>
                  setCategory({
                    ...category,
                    category_option_lists: newOptionList,
                  })
                }
                onRemove={handleRemoveOptionList}
                renderItem={(optionList) => optionList.name}
              />
            </CardContent>
          </Card>

          <div className="my-4 rounded-lg bg-white shadow">
            <CategoryOperatingHoursInput
              operatingHours={operatingHours}
              setOperatingHours={setOperatingHours}
              categoryId={category.id}
            />
          </div>

          <div className="mt-4 flex justify-end space-x-4">
            <Link
              to="/menu/categories"
              className="rounded-md bg-gray-500 px-4 py-2 text-white transition-colors hover:bg-gray-600"
            >
              {t("back.management.menu.category.cancelbutton")}
            </Link>
            <button
              type="submit"
              disabled={isSaving || !isDirty}
              className="rounded-md bg-red-500 px-4 py-2 text-white transition-colors hover:bg-red-600 disabled:bg-gray-400"
            >
              {isSaving
                ? t("back.management.menu.category.updating")
                : t("back.management.menu.category.updatebutton")}
            </button>
          </div>
        </form>

        <OptionListSelectionModal
          isOpen={isOptionListModalOpen}
          onClose={() => setIsOptionListModalOpen(false)}
          onSave={handleOptionListSave}
          initialSelectedOptionLists={category.category_option_lists}
        />
        <ContextualSaveBar
          isDirty={isDirty}
          onSave={handleSubmit}
          onDiscard={handleDiscard}
          isSaving={isSaving}
        />
      </div>
    </PageLoader>
  );
};

export default EditCategory;
